import { useTranslation } from 'react-i18next';
import { ChevronDown, Icon, Plus, type LucideIcon } from '../../ui/Icon';
import EmptyState from '../../ui/EmptyState';

interface Props {
  icon: LucideIcon;
  title: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  onAdd: () => void;
  /** "Add with AI" trigger, shown next to the plain add button. */
  extraAction?: React.ReactNode;
  /** Rendered when the section has no items. */
  empty: string;
  children?: React.ReactNode;
}

/**
 * One collapsible group of the Bookings tab (flights, hotels, transport, events):
 * header with count and actions, and either the items or an empty message.
 */
export function BookingSection({
  icon, title, count, collapsed, onToggle, onAdd, extraAction, empty, children,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 mb-3 overflow-hidden shadow-sm">
      <div className="flex items-center gap-2 px-4 sm:px-5 py-3 border-b border-slate-100 dark:border-slate-700">
        <button onClick={onToggle} className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer group">
          <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 group-hover:bg-teal-50 dark:group-hover:bg-teal-900/30 transition-colors">
            <Icon as={icon} size={16} className="text-slate-500 dark:text-slate-400 group-hover:text-teal-600 dark:group-hover:text-teal-400" />
          </div>
          <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm truncate">{title}</span>
          {count > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[10px] font-bold shrink-0">{count}</span>
          )}
          <Icon as={ChevronDown} size={16} className={`text-slate-400 dark:text-slate-500 shrink-0 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {extraAction}
          <button
            onClick={onAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold transition-colors cursor-pointer shrink-0"
          >
            <Icon as={Plus} size={14} /> {t('common.add')}
          </button>
        </div>
      </div>

      {!collapsed && (
        count === 0
          // No add button here — it would duplicate the header's own, right above.
          ? (
            <EmptyState size="inline" icon={icon} title={empty} />
          )
          : <div className="p-4 space-y-3">{children}</div>
      )}
    </div>
  );
}
